import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useMarketing } from "@/components/marketing/MarketingLayout";
import {
  ArrowRight, ArrowsClockwise, FileText, ShieldCheck, ChartLineUp, Robot,
  Plugs, UsersThree, LockKey, Coin, Rocket, ShieldStar, Lightning, GitBranch,
  Sparkle, Brain, ClockClockwise, Check, Circle,
} from "@phosphor-icons/react";

// ── Strict three-colour palette ─────────────────────────────────────────────
// Navy ink + gold accent + green status, on white surfaces only.
const NAVY  = "#0B1E3F";
const GOLD  = "#C8A24B";
const GREEN = "#1F6B4A";

// Integration logos shown in the marquee under the hero.
const INTEGRATIONS = [
  "Oracle Fusion", "Oracle EBS", "NetSuite", "Workday", "Microsoft Dynamics 365", "IFS",
];

// Word-stack — finance functions used as a vertical marquee that highlights
// the centre word as it rolls through.
const WORDSTACK = [
  "Reconciliation", "Month-End Close", "FP&A Forecasting", "AP Automation",
  "AR Collections", "Variance Analysis", "Document Intelligence", "Compliance Monitoring",
];

const STATS = [
  { n: "3", suffix: "days", l: "Month-end close", s: "down from 10 days" },
  { n: "75", suffix: "%",  l: "Manual touchpoints removed", s: "across AP, AR & close" },
  { n: "24/7", suffix: "", l: "Autonomous execution", s: "inside your ERP & EPM" },
];

const CAPS = [
  { icon: ArrowsClockwise, t: "Reconciliation AI", d: "Match transactions across bank, ledger and intercompany accounts — auto-resolve directly inside your ERP." },
  { icon: FileText,       t: "Document AI",        d: "Extract and classify invoices, remittances and contracts, then push structured data straight to your ERP." },
  { icon: ShieldCheck,    t: "Compliance AI",      d: "Continuously monitor SOX controls, spend limits and approvals with audit-ready logs." },
  { icon: ChartLineUp,    t: "Analytics AI",       d: "Turn live ERP & EPM data into variance narratives, forecasts and anomaly alerts." },
  { icon: Robot,          t: "Automation AI",      d: "Orchestrate end-to-end finance workflows with human-in-the-loop approval gates." },
];

const USE_CASES = [
  { c: "Close & Controllership", w: "Month-end close, reconciliations, accruals, variance analysis" },
  { c: "Procure to Pay",         w: "Invoice capture, 3-way matching, validation, payment controls" },
  { c: "Order to Cash",          w: "Cash application, collections, disputes, revenue validation" },
  { c: "Treasury & FP&A",        w: "Cash positioning, forecasting, scenario planning, working capital" },
  { c: "Tax & Compliance",       w: "Tax code validation, regulatory reporting, continuous monitoring" },
  { c: "Reporting & Analytics",  w: "Financial reports, KPI dashboards, ad-hoc finance queries" },
];

// Comparison: Finance teams today (problems, navy badge) → With Vaku (FIXED, green badge)
const COMPARE = [
  {
    sev: "HIGH",
    problem: "Month-end close takes 10+ days",
    pdetail: "Manual checklists, back-and-forth emails, and spreadsheet reconciliation eat your team's time every single month.",
    fix: "Close in 3 days, not 10",
    fdetail: "AI agents run your close checklist, flag exceptions, and post entries — no spreadsheets, no back-and-forth.",
  },
  {
    sev: "HIGH",
    problem: "6+ disconnected tools to manage",
    pdetail: "Close management, reconciliation, AP, AR, FP&A and doc processing each require a separate login, contract, and team to maintain.",
    fix: "One platform, 9 domains",
    fdetail: "A single workspace handles every finance workflow — one login, one contract, one audit trail for your whole team.",
  },
  {
    sev: "MED",
    problem: "No audit trail across systems",
    pdetail: "When auditors ask who approved what and when, answers are buried across tools, emails and shared drives.",
    fix: "Every action logged automatically",
    fdetail: "Full audit trail built into every agent workflow — who ran it, what changed, what was approved.",
  },
  {
    sev: "MED",
    problem: "$148K+ in point-solution spend",
    pdetail: "Each tool solves one problem in one workflow — budget is fragmented across vendors that don't integrate.",
    fix: "$100K+ saved annually",
    fdetail: "Replace your entire fragmented stack with a single subscription that costs a fraction of what you spend today.",
  },
];

// Core-advantage 4-card grid + headline stat card.
const CORE = [
  { eyebrow: "Control",     t: "Human in the loop",          d: "Every AI action goes through approval workflows. Journal entries are drafted, not posted. Exceptions are flagged, not hidden.", icon: LockKey },
  { eyebrow: "Speed",       t: "Live in under a week",        d: "Pre-built agents for common workflows. Connect your ERP, configure your rules, and start automating without a 6-month implementation.", icon: Lightning },
  { eyebrow: "Intelligence", t: "Agents that learn your business", d: "Vaku learns your chart of accounts, vendor patterns and approval hierarchies. Accuracy improves every cycle.", icon: Brain },
  { eyebrow: "Tenancy", t: "Isolated by tenant", d: "Per-tenant keys, per-tenant data. Your books never share a database with another customer's books — by design.", icon: GitBranch },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } };

const Reveal = ({ children, className = "", delay = 0, ...rest }) => (
  <motion.div className={className}
    initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
    variants={{ hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] } } }}
    {...rest}>
    {children}
  </motion.div>
);

const Overline = ({ children, tone = GOLD }) => (
  <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold"
       style={{ color: tone }}>{children}</div>
);

// ── Hero visualisation: integration chips orbit a central VAKU hub.
const HeroNetwork = () => {
  const nodes = [
    { name: "Oracle Fusion",   x: 18, y: 16 },
    { name: "Oracle EBS",      x: 82, y: 16 },
    { name: "Dynamics 365",    x: 8,  y: 50 },
    { name: "NetSuite",        x: 92, y: 50 },
    { name: "IFS",             x: 18, y: 84 },
    { name: "Workday",         x: 82, y: 84 },
  ];
  return (
    <motion.div
      className="relative w-full aspect-square max-w-[520px] mx-auto"
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      data-testid="hero-network">
      <div className="absolute inset-[12%] rounded-full"
           style={{ background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)` }} />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full overflow-visible">
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={NAVY} stopOpacity="0.55" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        {nodes.map((n, i) => (
          <g key={n.name}>
            <line x1={n.x} y1={n.y} x2={50} y2={50}
                  stroke="url(#line-grad)" strokeWidth="0.35"
                  strokeDasharray="0.6 1.2" opacity="0.85" />
            <circle r="0.9" fill={GOLD}>
              <animateMotion dur={`${3 + i * 0.4}s`} repeatCount="indefinite"
                             path={`M ${n.x} ${n.y} L 50 50`} />
              <animate attributeName="opacity"
                       values="0;1;1;0" dur={`${3 + i * 0.4}s`} repeatCount="indefinite" />
            </circle>
          </g>
        ))}
      </svg>

      {nodes.map((n, i) => (
        <motion.div key={n.name}
          className="absolute -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 rounded-full backdrop-blur-md bg-white border shadow-[0_8px_24px_rgba(11,30,63,0.08)] text-[11px] font-semibold whitespace-nowrap"
          style={{ left: `${n.x}%`, top: `${n.y}%`, color: NAVY, borderColor: `${NAVY}22` }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 + i * 0.08, duration: 0.5, ease: "easeOut" }}>
          {n.name}
        </motion.div>
      ))}

      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 sm:w-32 sm:h-32 rounded-full flex flex-col items-center justify-center text-white shadow-[0_22px_60px_rgba(11,30,63,0.35)]"
        style={{ background: `linear-gradient(140deg, ${NAVY} 0%, #142b5a 100%)` }}
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
        <Sparkle size={20} weight="fill" style={{ color: GOLD }} />
        <div className="font-serif text-2xl font-light mt-1 tracking-wider">VĀKU</div>
        <div className="text-[8px] font-mono tracking-[0.2em] uppercase mt-0.5"
             style={{ color: GOLD }}>AI Hub</div>
      </motion.div>

      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
        style={{ borderColor: `${GOLD}88`, width: "8rem", height: "8rem" }}
        animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeOut" }} />
    </motion.div>
  );
};

// ── Animated word-stack: cycles through finance functions and gold-highlights
// the centre word — replaces the static 8-card grid we had before.
const WordStack = () => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % WORDSTACK.length), 1600);
    return () => clearInterval(id);
  }, []);
  // Render 5 rows: idx-2, idx-1, idx, idx+1, idx+2 (centre highlighted)
  const around = [-2, -1, 0, 1, 2].map((o) => WORDSTACK[(idx + o + WORDSTACK.length * 2) % WORDSTACK.length]);
  return (
    <div className="relative h-[440px] sm:h-[520px] overflow-hidden" data-testid="word-stack">
      <div className="absolute inset-x-0 top-0 h-24 pointer-events-none z-10"
           style={{ background: `linear-gradient(to bottom, ${NAVY} 0%, transparent 100%)` }} />
      <div className="absolute inset-x-0 bottom-0 h-24 pointer-events-none z-10"
           style={{ background: `linear-gradient(to top, ${NAVY} 0%, transparent 100%)` }} />
      <div className="flex flex-col items-start justify-center h-full gap-3 sm:gap-5 px-2">
        {around.map((w, i) => {
          const isCentre = i === 2;
          return (
            <motion.div key={`${w}-${i}-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isCentre ? 1 : 0.32, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className={`font-serif font-light tracking-tight leading-none ${
                isCentre ? "text-4xl sm:text-6xl lg:text-7xl" : "text-2xl sm:text-3xl lg:text-4xl"
              }`}
              style={{ color: isCentre ? GOLD : "rgba(255,255,255,0.45)" }}>
              {w}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ── Close-checklist demo: ticks 4 items one-by-one, then resets.
const CHECKLIST = ["GL reconciliation", "Accruals posted", "Variance analysis", "Journal entries reviewed"];
const CloseChecklistDemo = () => {
  const [step, setStep] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % (CHECKLIST.length + 2)), 900);
    return () => clearInterval(id);
  }, []);
  const ticked = Math.min(step, CHECKLIST.length);
  const day = Math.max(3, 10 - ticked * 2);
  return (
    <div className="rounded-xl p-5 mt-6" style={{ background: "rgba(31,107,74,0.08)", border: `1px solid ${GREEN}44` }}
         data-testid="demo-close-checklist">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.2em]" style={{ color: GREEN }}>Close checklist</div>
        <div className="text-[10px] font-mono font-bold px-2 py-1 rounded" style={{ background: `${GREEN}1A`, color: GREEN, border: `1px solid ${GREEN}55` }}>
          Day {day}
        </div>
      </div>
      <div className="space-y-2">
        {CHECKLIST.map((item, i) => {
          const done = i < ticked;
          return (
            <motion.div key={item}
              animate={{ background: done ? `${GREEN}1A` : "transparent" }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-3 px-3 py-2 rounded-md"
              style={{ border: `1px solid ${done ? `${GREEN}55` : `${NAVY}30`}` }}>
              {done
                ? <Check size={14} weight="bold" style={{ color: GREEN }} />
                : <Circle size={14} weight="regular" style={{ color: "rgba(255,255,255,0.35)" }} />}
              <span className="text-xs" style={{ color: done ? "#fff" : "rgba(255,255,255,0.55)" }}>{item}</span>
            </motion.div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
          <motion.div className="h-full" animate={{ width: `${(ticked / CHECKLIST.length) * 100}%` }}
                       transition={{ duration: 0.5 }} style={{ background: GREEN }} />
        </div>
        <span className="text-[10px] font-mono" style={{ color: "rgba(255,255,255,0.6)" }}>{ticked} / {CHECKLIST.length}</span>
      </div>
    </div>
  );
};

// ── AI-agent chat demo: multi-turn conversation that builds up over time.
const CHAT_SCRIPT = [
  { kind: "user",  body: "Run accruals for December close" },
  { kind: "typing" },
  { kind: "agent", body: (
      <>
        Analyzed <b style={{ color: "#fff" }}>1,284 GL entries</b> across <b style={{ color: "#fff" }}>4 entities</b>.<br/>
        <b style={{ color: GREEN }}>36 accruals found</b> totaling <b style={{ color: "#fff" }}>$486,250</b>.
        <div className="mt-2 pt-2 border-t flex items-center gap-2 text-[11px]" style={{ borderColor: `${GREEN}33`, color: GREEN }}>
          <Check size={12} weight="bold" /> Posted to Fusion SaaS. Awaiting approval.
        </div>
      </>
    ) },
  { kind: "user",  body: "Approve and send summary to CFO" },
  { kind: "typing" },
  { kind: "agent", body: (
      <>
        <b style={{ color: GREEN }}>Done.</b> 36 entries posted. Summary sent to CFO.
        <div className="mt-2 pt-2 border-t flex items-center gap-2 text-[11px]" style={{ borderColor: `${GREEN}33`, color: GREEN }}>
          <Check size={12} weight="bold" /> Audit trail saved. Checklist updated.
        </div>
      </>
    ) },
];
const AgentChatDemo = () => {
  const [step, setStep] = React.useState(0);
  React.useEffect(() => {
    const tick = () => setStep((s) => (s + 1) % (CHAT_SCRIPT.length + 2));
    const id = setInterval(tick, 1400);
    return () => clearInterval(id);
  }, []);
  const visible = CHAT_SCRIPT.slice(0, Math.min(step, CHAT_SCRIPT.length));
  const scrollRef = React.useRef(null);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [step]);
  return (
    <div className="mt-6 flex flex-col" data-testid="demo-agent-chat">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold"
             style={{ background: `${GOLD}1A`, border: `1px solid ${GOLD}55`, color: GOLD }}>
          VK
        </div>
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>Vaku Agent</span>
      </div>
      <div ref={scrollRef} className="flex flex-col gap-2 overflow-y-auto pr-1"
           style={{ maxHeight: 280, scrollBehavior: "smooth" }}>
        {visible.map((m, i) => {
          if (m.kind === "typing") {
            return (
              <motion.div key={`t-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex gap-1.5 px-2 py-1.5">
                {[0, 1, 2].map((d) => (
                  <motion.span key={d}
                    animate={{ opacity: [0.25, 1, 0.25] }}
                    transition={{ duration: 1.1, repeat: Infinity, delay: d * 0.18, ease: "easeInOut" }}
                    className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                ))}
              </motion.div>
            );
          }
          const isUser = m.kind === "user";
          return (
            <motion.div key={`m-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
              <div className="rounded-2xl px-3.5 py-2 text-sm max-w-[88%] leading-snug"
                   style={isUser
                     ? { background: `${GOLD}1A`, border: `1px solid ${GOLD}55`, color: "#fff",
                         borderTopRightRadius: 6 }
                     : { background: `${GREEN}0F`, border: `1px solid ${GREEN}55`, color: "rgba(255,255,255,0.92)",
                         borderTopLeftRadius: 6 }}>
                {m.body}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ── Integration hub demo: pulse-ring around a central VK puck + ERP chips.
const ERP_CHIPS = ["Oracle Fusion", "Oracle EBS", "Dynamics 365", "NetSuite", "IFS", "Workday"];
const IntegrationHubDemo = () => (
  <div className="mt-6 relative h-56 flex flex-col items-center justify-end gap-4" data-testid="demo-integration-hub">
    {/* hub */}
    <div className="relative">
      <motion.div className="absolute inset-0 rounded-full border"
        style={{ borderColor: `${GOLD}88` }}
        animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }} />
      <motion.div className="absolute inset-0 rounded-full border"
        style={{ borderColor: `${GOLD}66` }}
        animate={{ scale: [1, 1.7], opacity: [0.5, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 1.2 }} />
      <div className="relative w-16 h-16 rounded-full flex items-center justify-center font-bold text-sm"
           style={{ background: `${GOLD}1A`, border: `1px solid ${GOLD}88`, color: GOLD }}>
        VK
      </div>
    </div>
    {/* chips */}
    <div className="flex flex-wrap gap-2 justify-center max-w-md">
      {ERP_CHIPS.map((c, i) => (
        <motion.div key={c}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 * i, ease: "easeOut" }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono"
          style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${GREEN}44`, color: "rgba(255,255,255,0.85)" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
          {c}
        </motion.div>
      ))}
    </div>
  </div>
);

// ── Audit-trail demo: rows scroll in one by one and cycle.
const AUDIT_ROWS = [
  { t: "09:14", body: "GL recon completed, 642 matched",   pill: "Matched",  tone: GREEN },
  { t: "09:31", body: "36 accruals posted to Fusion SaaS", pill: "Posted",   tone: GREEN },
  { t: "09:33", body: "Approved by Karthikk K., Controller", pill: "Approved", tone: GOLD  },
  { t: "09:47", body: "Variance $46K flagged on COGS delta", pill: "Flagged",  tone: GOLD  },
  { t: "10:02", body: "Board summary sent to CFO",         pill: "Sent",     tone: GREEN },
];
const AuditTrailDemo = () => {
  const [count, setCount] = React.useState(1);
  React.useEffect(() => {
    const id = setInterval(() => setCount((c) => (c % AUDIT_ROWS.length) + 1), 1100);
    return () => clearInterval(id);
  }, []);
  const visible = AUDIT_ROWS.slice(0, count);
  return (
    <div className="rounded-xl p-4 mt-6" style={{ background: "rgba(31,107,74,0.06)", border: `1px solid ${GREEN}44` }}
         data-testid="demo-audit-trail">
      <div className="flex items-center gap-2 mb-3 pb-3 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em]" style={{ color: GREEN }}>Live audit trail</span>
      </div>
      <div className="space-y-2">
        {visible.map((r) => (
          <motion.div key={`${r.t}-${r.body}`}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="flex items-center gap-3 text-[11px]">
            <span className="font-mono" style={{ color: "rgba(255,255,255,0.45)" }}>{r.t}</span>
            <span className="flex-1 truncate" style={{ color: "rgba(255,255,255,0.85)" }}>{r.body}</span>
            <span className="text-[9px] font-mono font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded shrink-0"
                  style={{ background: `${r.tone}1A`, color: r.tone, border: `1px solid ${r.tone}55` }}>
              {r.pill}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ── "Built for how finance teams actually work" — 4 demo cards on a navy band.
const HOW_CARDS = [
  { eyebrow: "Month-end close", t: "Close in 3 days.",       d: "AI agents run your checklist, post entries, and flag exceptions automatically every month.", Demo: CloseChecklistDemo },
  { eyebrow: "AI agents",       t: "AI that executes.",       d: "Tell it to run a reconciliation or post accruals. It does it inside your ERP.",              Demo: AgentChatDemo      },
  { eyebrow: "Integrations",    t: "16 ERPs. One platform.",  d: "Native connectors that read and write directly. No CSV exports. No sync delays.",            Demo: IntegrationHubDemo },
  { eyebrow: "Compliance",      t: "Every action recorded.",  d: "Who ran it, what changed, when it was approved. Ready to export for your auditors.",         Demo: AuditTrailDemo     },
];

export default function HomePage() {
  const { openDemo } = useMarketing();

  return (
    <>
      {/* ===================== HERO ============================================ */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full pointer-events-none"
             style={{ background: `radial-gradient(circle, ${GOLD}22, transparent 60%)`, filter: "blur(60px)" }} />
        <div className="absolute -bottom-40 -right-32 w-[600px] h-[600px] rounded-full pointer-events-none"
             style={{ background: `radial-gradient(circle, ${GREEN}14, transparent 70%)`, filter: "blur(80px)" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div className="lg:col-span-7"
            initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp}
              className="font-serif text-[clamp(2.6rem,7vw,5.5rem)] font-light tracking-tighter leading-[1.02]"
              style={{ color: NAVY }} data-testid="hero-h1">
              AI Super Agent for{" "}
              <span className="italic" style={{ color: GOLD }}>
                Finance and Accounting Teams
              </span>
            </motion.h1>
            <motion.p variants={fadeUp}
              className="mt-8 text-lg sm:text-xl leading-relaxed font-light max-w-2xl"
              style={{ color: `${NAVY}B3` }}>
              One AI replacing point solutions across{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>Controllership</span>,{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>FP&amp;A</span>,{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>AP</span>,{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>AR</span>,{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>Tax</span>, and{" "}
              <span style={{ color: NAVY, fontWeight: 500 }}>Treasury</span>. Plugs directly into your ERP.
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-3">
              <button onClick={openDemo} data-testid="hero-book-demo"
                className="group rounded-full px-7 py-3.5 font-semibold transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 text-white"
                style={{ background: NAVY,
                          boxShadow: "0 12px 32px rgba(11,30,63,0.20)" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = GOLD)}
                onMouseLeave={(e) => (e.currentTarget.style.background = NAVY)}>
                Book a demo <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="#how-it-works" data-testid="hero-see-how"
                className="rounded-full px-7 py-3.5 font-semibold border inline-flex items-center gap-2 transition-colors bg-white"
                style={{ color: NAVY, borderColor: `${NAVY}25` }}>
                See how it works
              </a>
            </motion.div>
          </motion.div>

          <div className="lg:col-span-5">
            <HeroNetwork />
          </div>
        </div>
      </section>

      {/* ===================== INTEGRATIONS marquee ============================ */}
      <div className="border-y bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="text-center pt-10 pb-5">
          <Overline>Works with the ERP, EPM or HCM tool you already run</Overline>
        </div>
        <Marquee gradient gradientColor="#FFFFFF" gradientWidth={120} speed={45} pauseOnHover>
          {INTEGRATIONS.concat(INTEGRATIONS).map((i, idx) => (
            <div key={idx} className="mx-12 text-2xl sm:text-3xl font-serif font-light cursor-default transition-colors"
                 style={{ color: `${NAVY}80` }}
                 onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                 onMouseLeave={(e) => (e.currentTarget.style.color = `${NAVY}80`)}>
              {i}
            </div>
          ))}
        </Marquee>
        <div className="pb-10" />
      </div>

      {/* ===================== WORD STACK: "Companies are using Vaku for" ====== */}
      <section className="py-20 sm:py-28 border-t" style={{ background: NAVY, borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <Overline tone={GOLD}>What teams run on Vaku</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-5 leading-[1.1] text-white">
              Companies are using <span className="italic" style={{ color: GOLD }}>Vaku</span> for
            </h2>
            <p className="mt-6 text-base font-light leading-relaxed max-w-md" style={{ color: "rgba(255,255,255,0.65)" }}>
              Eight finance workflows. One agent. Live inside your ERP — no rip-and-replace, no CSV exports, no sync delays.
            </p>
          </Reveal>
          <WordStack />
        </div>
      </section>

      {/* ===================== COMPARISON: fragmented tools vs one AI =========== */}
      <section id="how-it-works" className="py-24 sm:py-32 border-t bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-14 text-center mx-auto">
            <Overline>The Vaku difference</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05]" style={{ color: NAVY }}>
              Replace fragmented tools with one AI <span className="italic" style={{ color: GOLD }}>system of action</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg font-light leading-relaxed" style={{ color: `${NAVY}A6` }}>
              Finance teams juggle disconnected point solutions that don&apos;t talk to each other.
              Vaku replaces them by executing workflows end-to-end.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
            {/* LEFT — Finance teams today */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: NAVY }} />
                <Overline tone={NAVY}>Finance teams today</Overline>
              </div>
              <div className="space-y-4" data-testid="compare-problems">
                {COMPARE.map((c) => (
                  <Reveal key={c.problem}
                    className="rounded-2xl p-6 bg-white transition-all hover:-translate-y-0.5"
                    style={{ border: `1px solid ${NAVY}1F`, boxShadow: "0 4px 14px rgba(11,30,63,0.04)" }}>
                    <div className="flex items-start gap-4">
                      <span className="text-[10px] font-mono font-bold tracking-wider uppercase px-2 py-1 rounded shrink-0"
                            style={{ background: `${NAVY}10`, color: NAVY, border: `1px solid ${NAVY}25` }}>
                        {c.sev}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base sm:text-lg leading-snug" style={{ color: NAVY }}>{c.problem}</h3>
                        <p className="mt-2 text-sm font-light leading-relaxed" style={{ color: `${NAVY}99` }}>{c.pdetail}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* RIGHT — With Vaku */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: GREEN }} />
                <Overline tone={GREEN}>With Vaku</Overline>
              </div>
              <div className="space-y-4" data-testid="compare-fixes">
                {COMPARE.map((c) => (
                  <Reveal key={c.fix} delay={0.05}
                    className="rounded-2xl p-6 transition-all hover:-translate-y-0.5"
                    style={{ background: `${GREEN}08`, border: `1px solid ${GREEN}33`,
                              boxShadow: `0 4px 14px ${GREEN}14` }}>
                    <div className="flex items-start gap-4">
                      <span className="text-[10px] font-mono font-bold tracking-wider uppercase px-2 py-1 rounded shrink-0"
                            style={{ background: `${GREEN}1A`, color: GREEN, border: `1px solid ${GREEN}55` }}>
                        Fixed
                      </span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-base sm:text-lg leading-snug" style={{ color: NAVY }}>{c.fix}</h3>
                        <p className="mt-2 text-sm font-light leading-relaxed" style={{ color: `${NAVY}99` }}>{c.fdetail}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS: 4 animated demo cards ============= */}
      <section className="py-20 sm:py-28 border-t" style={{ background: NAVY, borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-12 mx-auto text-center">
            <Overline tone={GREEN}>How it works</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05] text-white">
              Built for how finance teams <span className="italic" style={{ color: GOLD }}>actually work</span>.
            </h2>
            <p className="mt-5 text-base sm:text-lg font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
              From close to reporting, Vaku executes the work. Not just the chat.
            </p>
          </Reveal>

          <motion.div className="grid md:grid-cols-2 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            data-testid="how-it-works-grid">
            {HOW_CARDS.map(({ eyebrow, t, d, Demo }) => (
              <motion.div key={t} variants={fadeUp}
                data-testid={`how-card-${eyebrow.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                className="rounded-2xl p-7 sm:p-8 flex flex-col transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${GOLD}33`,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.18)" }}>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: GREEN }}>{eyebrow}</div>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-medium tracking-tight text-white">{t}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>{d}</p>
                <Demo />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===================== CORE ADVANTAGE 4-card grid ====================== */}
      <section className="py-24 sm:py-32 border-t" style={{ background: NAVY, borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* headline stat card */}
          <Reveal className="rounded-2xl p-8 sm:p-10 mb-6 grid md:grid-cols-2 gap-8 items-center"
            style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${GOLD}40` }}>
            <div>
              <Overline tone={GOLD}>Core advantage</Overline>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-4 leading-[1.1] text-white">
                ERP-native AI, <span className="italic" style={{ color: GOLD }}>not a bolt-on</span>
              </h2>
              <p className="mt-5 text-base font-light leading-relaxed text-white/70 max-w-md">
                Vaku reads and writes directly to your ERP. No CSV exports, no middleware,
                no sync delays. Your agents operate on live data with full audit trails.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 sm:gap-6 self-start">
              {[
                { n: "0",    l: "CSV exports" },
                { n: "0",    l: "Sync delays" },
                { n: "100%", l: "Audit trail" },
              ].map((s) => (
                <div key={s.l} className="text-center">
                  <div className="font-serif font-light leading-none text-[clamp(2.5rem,5vw,4rem)]" style={{ color: GREEN }}>{s.n}</div>
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/55">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* 4-card grid */}
          <motion.div className="grid md:grid-cols-2 gap-5"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
            data-testid="core-advantage-grid">
            {CORE.map(({ icon: Icon, eyebrow, t, d }) => (
              <motion.div key={t} variants={fadeUp}
                data-testid={`core-${eyebrow.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                className="rounded-2xl p-8 transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${NAVY}99` }}>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl"
                     style={{ background: `${GOLD}1A`, border: `1px solid ${GOLD}55`, color: GOLD }}>
                  <Icon size={22} weight="duotone" />
                </div>
                <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: GOLD }}>{eyebrow}</div>
                <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-medium tracking-tight text-white">{t}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/65">{d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===================== STATS =========================================== */}
      <section className="py-12 sm:py-16 relative overflow-hidden bg-white">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[200px] rounded-full pointer-events-none"
             style={{ background: `${GOLD}14`, filter: "blur(100px)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-8">
            <Overline tone={GOLD}>By the numbers</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-3 leading-[1.1]"
                style={{ color: NAVY }}>
              Outcomes finance leaders <span className="italic" style={{ color: GOLD }}>measure</span>.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 0.1} className="border-t pt-5" style={{ borderColor: `${NAVY}15` }}>
                <div className="font-serif font-light leading-none tracking-tighter" style={{ color: GOLD }}>
                  <span className="text-[clamp(2.5rem,5vw,4rem)]">{s.n}</span>
                  {s.suffix && <span className="text-xl sm:text-2xl ml-1" style={{ color: `${GOLD}CC` }}>{s.suffix}</span>}
                </div>
                <div className="mt-3 text-base font-light" style={{ color: NAVY }}>{s.l}</div>
                <div className="mt-1 text-xs font-mono inline-flex items-center gap-2" style={{ color: GREEN }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                  {s.s}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== CAPABILITIES ==================================== */}
      <section className="py-16 sm:py-20 border-t" style={{ background: NAVY, borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-10">
            <Overline tone={GOLD}>Five AI capabilities · one platform</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mt-3 leading-[1.1] text-white">
              Built for the modern <span className="italic" style={{ color: GOLD }}>finance back office</span>.
            </h2>
          </Reveal>
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
            variants={stagger}>
            {CAPS.map((c) => (
              <motion.div key={c.t} variants={fadeUp}
                className="group relative overflow-hidden rounded-2xl p-5 flex flex-col gap-3 transition-all hover:-translate-y-1"
                style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${GOLD}33`,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.18)" }}>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg"
                     style={{ background: `${GOLD}1A`, border: `1px solid ${GOLD}55`, color: GOLD }}>
                  <c.icon size={18} weight="duotone" />
                </div>
                <h3 className="font-serif text-lg font-medium tracking-tight text-white">{c.t}</h3>
                <p className="text-xs leading-relaxed font-light text-white/65">{c.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===================== USE CASES ======================================= */}
      <section className="py-24 sm:py-32 border-t bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="max-w-3xl mb-20">
            <Overline tone={GOLD}>Where Vaku works today</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4 leading-[1.05]"
                style={{ color: NAVY }}>
              Six scenarios. <span className="italic" style={{ color: GOLD }}>One agent.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="lg:sticky lg:top-24 self-start">
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden p-10 flex flex-col justify-between"
                   style={{ background: `linear-gradient(160deg, ${NAVY} 0%, #142b5a 100%)` }}>
                <svg className="absolute inset-0 w-full h-full opacity-[0.18]" aria-hidden>
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#fff" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />
                </svg>
                <div className="relative">
                  <Overline tone={GOLD}>Built for CFOs &amp; their controllers</Overline>
                  <div className="mt-4 font-serif text-3xl sm:text-4xl font-light text-white leading-tight">
                    From close to forecast — without a finger on the mouse.
                  </div>
                </div>
                <div className="relative grid grid-cols-3 gap-3">
                  {[ArrowsClockwise, FileText, ShieldCheck, ChartLineUp, Robot, Plugs].map((Icon, i) => (
                    <div key={i} className="aspect-square rounded-lg flex items-center justify-center"
                         style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                      <Icon size={22} weight="duotone" color={GOLD} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-12">
              {USE_CASES.map((u, i) => (
                <Reveal key={u.c} delay={i * 0.05}
                        className="border-l-2 pl-6 lg:pl-8 transition-colors hover:border-current"
                        style={{ borderColor: `${GOLD}66` }}>
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] font-semibold"
                       style={{ color: GOLD }}>0{i + 1} · Scenario</div>
                  <h3 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-2" style={{ color: NAVY }}>{u.c}</h3>
                  <p className="mt-3 text-base font-light leading-relaxed" style={{ color: `${NAVY}A6` }}>{u.w}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== TRUST: Built for enterprise finance data ======== */}
      <section className="py-24 sm:py-32 border-t" style={{ background: NAVY, borderColor: `${NAVY}10` }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <Overline tone={GREEN}>Security &amp; compliance</Overline>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter mt-6 leading-[1.02] text-white">
              Built for <span className="italic" style={{ color: GOLD }}>enterprise finance data</span>
            </h2>
            <p className="mt-12 sm:mt-16 text-lg sm:text-xl font-light leading-relaxed max-w-3xl mx-auto"
               style={{ color: "rgba(255,255,255,0.72)" }}>
              <span style={{ color: GOLD, fontWeight: 500 }}>AES-256 encryption</span>,{" "}
              <span style={{ color: GOLD, fontWeight: 500 }}>TLS 1.3</span> in transit,{" "}
              <span style={{ color: GOLD, fontWeight: 500 }}>SOC 2 Type II</span> compliance, human-in-the-loop
              approval for every transaction. Audit trails log every action.{" "}
              <span style={{ color: GOLD, fontWeight: 500 }}>Data residency</span> in{" "}
              <span style={{ color: "#fff" }}>US, EU, UAE, KSA</span> and other countries
              with isolated tenants.
            </p>
            <div className="mt-12 inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3" data-testid="trust-pills">
              {["US", "EU", "UAE", "KSA", "+ more"].map((r) => (
                <span key={r} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em]"
                      style={{ color: "rgba(255,255,255,0.7)" }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} />
                  {r}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===================== PRICING TEASER ================================== */}
      <section className="py-28 sm:py-36 border-t bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] font-semibold" style={{ color: GOLD }}>
              A platform, not a project
            </div>
            <h2 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02]" style={{ color: NAVY }}>
              Start in <span className="italic" style={{ color: GOLD }}>weeks</span>.<br />
              See payback in <span className="italic" style={{ color: GOLD }}>months</span>.
            </h2>
            <p className="mt-8 text-lg sm:text-xl font-light max-w-2xl mx-auto leading-relaxed" style={{ color: `${NAVY}B3` }}>
              Pricing scales with usage — not with seats. We&apos;ll build a TCO model for your finance team during the first 30-minute demo.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button onClick={openDemo} data-testid="pricing-book-demo"
                className="group rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 inline-flex items-center gap-2 text-white"
                style={{ background: NAVY,
                          boxShadow: "0 12px 32px rgba(11,30,63,0.25)" }}>
                Book a Demo <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/products" className="font-semibold inline-flex items-center gap-2 group" style={{ color: NAVY }}>
                Explore products <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4" style={{ color: `${NAVY}80` }}>
              <div className="flex items-center gap-2"><Coin size={16} weight="duotone" style={{ color: GOLD }} /><span className="text-sm font-mono">Usage-based</span></div>
              <div className="flex items-center gap-2"><Rocket size={16} weight="duotone" style={{ color: GOLD }} /><span className="text-sm font-mono">Live in 6 weeks</span></div>
              <div className="flex items-center gap-2"><Plugs size={16} weight="duotone" style={{ color: GOLD }} /><span className="text-sm font-mono">No rip-and-replace</span></div>
              <div className="flex items-center gap-2"><ClockClockwise size={16} weight="duotone" style={{ color: GREEN }} /><span className="text-sm font-mono">SOC 2 in flight</span></div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
